import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" }
};

const LEAVE_TYPES = ["Annual", "Sick", "Maternity", "Paternity", "Study", "Compassionate", "Unpaid"];

const BLANK = { staffId: "", leaveType: "Annual", startDate: "", endDate: "", reason: "" };

class LeaveList extends Component {
  state = {
    leaves: [],
    staff: [],
    loading: true,
    open: false,
    form: { ...BLANK },
    saving: false,
    error: null
  };

  componentDidMount() {
    Promise.all([this.fetchLeaves(), this.fetchStaff()]);
  }

  fetchLeaves = async () => {
    try {
      const res = await api.get("/hr/leaves?limit=100");
      const d = res.data.data || res.data;
      this.setState({ leaves: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  fetchStaff = async () => {
    try {
      const res = await api.get("/staff?limit=100");
      const d = res.data.data || res.data;
      this.setState({ staff: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  handleChange = field => e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, [field]: v } })); };

  handleSave = async () => {
    const { form } = this.state;
    if (!form.staffId || !form.startDate || !form.endDate) {
      this.setState({ error: "Staff, start date, and end date are required." }); return;
    }
    this.setState({ saving: true, error: null });
    try {
      await api.post("/hr/leaves", form);
      this.setState({ open: false, saving: false });
      this.fetchLeaves();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed.", saving: false });
    }
  };

  handleDecision = async (id, status) => {
    try {
      await api.patch(`/hr/leaves/${id}`, { status });
      this.fetchLeaves();
    } catch (e) { /* silent */ }
  };

  render() {
    const { classes } = this.props;
    const { leaves, staff, loading, open, form, saving, error } = this.state;

    const computeDuration = l => {
      if (!l.startDate || !l.endDate) return "—";
      const days = Math.max(1, Math.ceil((new Date(l.endDate) - new Date(l.startDate)) / 86400000) + 1);
      return `${days} day${days !== 1 ? "s" : ""}`;
    };

    const columns = [
      {
        id: "staff",
        header: "Staff",
        accessorFn: l => {
          const staffMember = l.staff || {};
          return `${staffMember.firstName || ""} ${staffMember.lastName || ""}`.trim() || "—";
        }
      },
      { id: "leaveType", header: "Leave Type", accessorFn: l => l.leaveType || "—" },
      { id: "start", header: "Start", accessorFn: l => l.startDate || "—" },
      { id: "end", header: "End", accessorFn: l => l.endDate || "—" },
      { id: "duration", header: "Duration", accessorFn: l => computeDuration(l) },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const status = info.row.original.status || "pending";
          const tone = status === "approved" ? "success" : status === "rejected" ? "danger" : "warning";
          return <Pill tone={tone}>{status}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => {
          const l = info.row.original;
          if (l.status !== "pending") return null;
          return (
            <div style={{ display: "flex", gap: "4px" }}>
              <Button size="sm" color="success" simple onClick={() => this.handleDecision(l.id, "approved")}>Approve</Button>
              <Button size="sm" color="danger" simple onClick={() => this.handleDecision(l.id, "rejected")}>Reject</Button>
            </div>
          );
        }
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Leave Management</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    Staff leave applications and approvals
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button size="sm" color="white" onClick={() => this.setState({ open: true, form: { ...BLANK }, error: null })}>
                    + Apply for Leave
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar tabs={[]} actions={[]} />
              <DataTable
                columns={columns}
                data={leaves}
                isLoading={loading}
                emptyState="No leave applications found."
              />
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title="Apply for Leave" />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <FormControl className={classes.formControl}>
              <InputLabel>Staff Member *</InputLabel>
              <Select value={form.staffId} onChange={this.handleChange("staffId")}>
                <MenuItem value="">— Select Staff —</MenuItem>
                {staff.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName} — {s.role || ""}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Leave Type</InputLabel>
              <Select value={form.leaveType} onChange={this.handleChange("leaveType")}>
                {LEAVE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <GridContainer>
              <GridItem xs={6}>
                <CustomDatePicker labelText="Start Date *" id="leaveStart" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.startDate, onChange: this.handleChange("startDate") }} />
              </GridItem>
              <GridItem xs={6}>
                <CustomDatePicker labelText="End Date *" id="leaveEnd" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.endDate, onChange: this.handleChange("endDate") }} />
              </GridItem>
            </GridContainer>
            <CustomInput labelText="Reason" id="leaveReason" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.reason, onChange: this.handleChange("reason"), multiline: true, rows: 3 }} />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Submit"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

LeaveList.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(LeaveList));
